import React from "react";
import { WOEmailsTab } from "../components/WOEmailsTab/WOEmailsTab";

// ─── Types ────────────────────────────────────────────────────────────────────

type TicketTab = "External Chat" | "Comments" | "Email" | "Activity Log";

interface TicketDetail {
  id: string;
  number: string;
  title: string;
  company: string;
  status: string;
  statusColor: { bg: string; color: string };
  createdAt: string;
  createdBy: { initials: string; name: string };
  ticketType: string;
  assignee: { initials: string; name: string };
  team: string;
}

interface ChatMessage {
  id: string;
  sender: string;
  senderInitials: string;
  text: string;
  time: string;
  isOutgoing: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const TICKET: TicketDetail = {
  id: "1",
  number: "JS812300HNW00",
  title: "Asset Repair Request",
  company: "Acme International",
  status: "OPEN",
  statusColor: { bg: "#ccefeb", color: "#00b09b" },
  createdAt: "15/10/2025",
  createdBy: { initials: "MZ", name: "Michael Ziegler" },
  ticketType: "Service Request",
  assignee: { initials: "SB", name: "Sophia Brown" },
  team: "BERLIN",
};

const EXTERNAL_CHAT_MESSAGES: ChatMessage[] = [
  {
    id: "1", sender: "Michael Ziegler", senderInitials: "MZ",
    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    time: "11:52 AM", isOutgoing: true,
  },
];

const COMMENT_MESSAGES: ChatMessage[] = [
  {
    id: "c1", sender: "Michael Ziegler", senderInitials: "MZ",
    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    time: "11:52 AM", isOutgoing: true,
  },
  {
    id: "c2", sender: "James McDonald", senderInitials: "JM",
    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    time: "11:52 AM", isOutgoing: false,
  },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round">
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="11" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="11" width="6" height="6" rx="1" />
    <rect x="11" y="11" width="6" height="6" rx="1" />
  </svg>
);

const SlashIcon = () => (
  <span style={{ color: "#a2aab8", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-family-base)" }}>/</span>
);

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="#748094">
    <circle cx="3" cy="8" r="1.2" />
    <circle cx="8" cy="8" r="1.2" />
    <circle cx="13" cy="8" r="1.2" />
  </svg>
);

const DotsIconLg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="#748094">
    <circle cx="4" cy="10" r="1.5" />
    <circle cx="10" cy="10" r="1.5" />
    <circle cx="16" cy="10" r="1.5" />
  </svg>
);

const TicketTypeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0517f8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <circle cx="7" cy="7" r="1" fill="#0517f8" stroke="none" />
  </svg>
);

const FacilityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#0517f8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="14" height="11" rx="1.5" />
    <path d="M6 5V4a3 3 0 016 0v1M6 9.5h6M6 12.5h4" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round">
    <path d="M5 12.5l5-5 5 5" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round">
    <path d="M5 7.5l5 5 5-5" />
  </svg>
);

const IdIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M5 7h2M5 9.5h6M9 7h2" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
    <path d="M2 6.5h12M5 1.5v2M11 1.5v2" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="5" r="3" />
    <path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const TeamIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="5" r="2.5" />
    <path d="M1 13c0-2.8 2.2-5 5-5s5 2.2 5 5" />
    <circle cx="12" cy="5" r="2" />
    <path d="M14 13c0-2.2-1.8-4-4-4" />
  </svg>
);

const FieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="12" height="8" rx="1.5" />
    <path d="M5 8h6" />
  </svg>
);

const AssetImagePlaceholder = () => (
  <div style={{
    width: 32, height: 32, borderRadius: 8,
    background: "#e6e8fe", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="12" height="12" rx="2" />
      <path d="M5 8h6M8 5v6" />
    </svg>
  </div>
);

const AttachmentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13.5 7.5l-5.5 5.5a4 4 0 01-5.7-5.7L8 1.6a2.5 2.5 0 013.5 3.5L5.8 10.8a1 1 0 01-1.4-1.4L10 3.8" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="#f97316" strokeWidth="1.4" />
    <path d="M8 7v4" stroke="#f97316" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="8" cy="5" r="0.8" fill="#f97316" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#748094" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="5.5" />
    <path d="M7 4.5V7l1.5 1.5" />
  </svg>
);

const AlertCircleIcon = ({ color = "#ff7a00" }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <circle cx="7.5" cy="7.5" r="7" fill={color} />
    <path d="M7.5 4v4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="7.5" cy="10.5" r="0.8" fill="white" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2L7.5 8.5M14 2L9.5 14l-2-5.5L2 6.5l12-4.5z" />
  </svg>
);

// ─── Sub-components ───────────────────────────────────────────────────────────

function Avatar({ initials, size = 20 }: { initials: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "#e6e8fe", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.5, fontWeight: 700, color: "#0517f8",
      fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px",
    }}>
      {initials}
    </div>
  );
}

function StatusBadge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: bg, color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function SidebarSection({
  title,
  action,
  collapsible = false,
  collapsed = false,
  onToggle,
  children,
}: {
  title: string;
  action?: { label: string; onClick: () => void };
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div style={{
      borderBottom: "1px solid var(--color-border-primary)",
      padding: "16px 0 16px 16px",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: children && !collapsed ? 16 : 0,
        paddingRight: 16,
      }}>
        <span style={{
          flex: 1, fontSize: 14, fontWeight: 700, letterSpacing: "-0.28px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
        }}>
          {title}
        </span>
        {action && (
          <button
            onClick={action.onClick}
            style={{
              border: "none", background: "transparent", cursor: "pointer", padding: "4px",
              fontSize: 12, fontWeight: 700, color: "#0517f8",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
            }}
          >
            {action.label}
          </button>
        )}
        {collapsible && (
          <button
            onClick={onToggle}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}
          >
            {collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
          </button>
        )}
      </div>
      {!collapsed && children}
    </div>
  );
}

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      <div style={{
        width: 140, display: "flex", gap: 10, alignItems: "center",
        padding: "8px 0", flexShrink: 0,
      }}>
        {icon}
        <span style={{
          fontSize: 12, fontWeight: 500, color: "#748094",
          fontFamily: "var(--font-family-base)", overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {label}
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: "6px 8px", borderRadius: 4 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Chat Message Bubble ──────────────────────────────────────────────────────

function ChatBubble({ message }: { message: ChatMessage }) {
  if (message.isOutgoing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%" }}>
        <div style={{
          background: "#e6e8fe",
          borderRadius: "12px 12px 0 12px",
          padding: 12, maxWidth: 520,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              flex: 1, fontSize: 14, fontWeight: 700, lineHeight: "20px",
              letterSpacing: "-0.28px", color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)",
            }}>
              {message.sender}
            </span>
            <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
              <DotsIconLg />
            </button>
          </div>
          <p style={{
            fontSize: 14, fontWeight: 500, lineHeight: "20px",
            color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
            margin: 0,
          }}>
            {message.text}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <span style={{
              fontSize: 12, fontWeight: 500, color: "#748094",
              fontFamily: "var(--font-family-base)",
            }}>
              {message.time}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
      <div style={{
        background: "#f3f4f6",
        borderRadius: "12px 12px 12px 0",
        padding: 12, maxWidth: 520,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Avatar initials={message.senderInitials} size={20} />
          <span style={{
            flex: 1, fontSize: 14, fontWeight: 700, lineHeight: "20px",
            letterSpacing: "-0.28px", color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
          }}>
            {message.sender}
          </span>
        </div>
        <p style={{
          fontSize: 14, fontWeight: 500, lineHeight: "20px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
          margin: 0,
        }}>
          {message.text}
        </p>
        <span style={{
          fontSize: 12, fontWeight: 500, color: "#748094",
          fontFamily: "var(--font-family-base)",
        }}>
          {message.time}
        </span>
      </div>
    </div>
  );
}

// ─── Chat Input ───────────────────────────────────────────────────────────────

function ChatInput({ onSend }: { onSend: (text: string) => void }) {
  const [value, setValue] = React.useState("");
  return (
    <div style={{
      borderTop: "1px solid var(--color-border-primary)",
      padding: "16px 24px",
      display: "flex", alignItems: "center", gap: 16,
      background: "white", flexShrink: 0,
    }}>
      <input
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && value.trim()) { onSend(value.trim()); setValue(""); } }}
        placeholder="Write your message here..."
        style={{
          flex: 1, border: "none", outline: "none", background: "transparent",
          fontSize: 14, fontWeight: 500, lineHeight: "20px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <button style={{
          width: 34, height: 34, border: "1px solid var(--color-border-primary)",
          borderRadius: 8, background: "white", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <AttachmentIcon />
        </button>
        <button
          onClick={() => { if (value.trim()) { onSend(value.trim()); setValue(""); } }}
          style={{
            width: 34, height: 34, border: "none",
            borderRadius: 8, background: "#0517f8", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Tab Contents ─────────────────────────────────────────────────────────────

function ExternalChatTab({ messages, onSend }: { messages: ChatMessage[]; onSend: (text: string) => void }) {
  return (
    <>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ textAlign: "center" }}>
          <span style={{
            fontSize: 12, fontWeight: 500, color: "#748094",
            fontFamily: "var(--font-family-base)",
          }}>
            Today, 15 April 2026
          </span>
        </div>
        {messages.map(m => <ChatBubble key={m.id} message={m} />)}
      </div>
      <ChatInput onSend={onSend} />
    </>
  );
}

// ─── SLA Handoff Modal ────────────────────────────────────────────────────────

function SLAHandoffModal({
  onConfirm,
  onClose,
  hasReplied,
  ticketStatus,
  slaStartRef,
}: {
  onConfirm: () => void;
  onClose: () => void;
  hasReplied: boolean;
  ticketStatus: string;
  slaStartRef: React.MutableRefObject<number>;
}) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Ticket SLA computations
  const responseElapsed  = 3 * 60 + 17 + (now - slaStartRef.current) / 60000;
  const resolutionElapsed = 21 * 60 + 17 + (now - slaStartRef.current) / 60000;
  const responseTarget   = 4 * 60;
  const resolutionTarget = 24 * 60;

  const responseRemaining   = responseTarget - responseElapsed;
  const resolutionRemaining = resolutionTarget - resolutionElapsed;

  function fmtElapsed(mins: number) {
    const h = Math.floor(mins / 60);
    const m = Math.floor(mins % 60);
    return h > 0 ? `${h}h ${m}m elapsed` : `${m}m elapsed`;
  }

  function fmtRemaining(mins: number) {
    const breached = mins <= 0;
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = Math.floor(abs % 60);
    const s = Math.floor((abs * 60) % 60);
    return breached
      ? `Breached by ${h}h ${m}m ${String(s).padStart(2, "0")}s`
      : `${h}h ${m}m ${String(s).padStart(2, "0")}s remaining`;
  }

  function statusColor(remaining: number, met: boolean) {
    if (met) return { text: "#059669", bg: "#D1FAE5", border: "#6EE7B7" };
    if (remaining <= 0) return { text: "#DC2626", bg: "#FEE2E2", border: "#FCA5A5" };
    if (remaining / (remaining + (24 * 60 - remaining)) < 0.15) return { text: "#D97706", bg: "#FEF3C7", border: "#FCD34D" };
    return { text: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" };
  }

  const responseMet    = hasReplied;
  const resolutionMet  = ticketStatus === "CLOSED";
  const respColor      = statusColor(responseRemaining, responseMet);
  const resColor       = statusColor(resolutionRemaining, resolutionMet);

  const font = "var(--font-family-base)";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(23,43,77,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(23,43,77,0.22)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", fontFamily: font, letterSpacing: "-0.3px", marginBottom: 4 }}>
              Create Work Order from Ticket
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#6B7280", fontFamily: font }}>
              Review how SLA timers will transfer before confirming.
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "1.5px solid #E5E7EB", borderRadius: 8, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
          </button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20, overflowY: "auto" }}>

          {/* ── Ticket SLA status ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", fontFamily: font, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
              Ticket SLA at conversion
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

              {/* First Response */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", border: `1px solid ${respColor.border}`, borderRadius: 9, background: respColor.bg }}>
                <div style={{ flexShrink: 0 }}>
                  {responseMet ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="9" fill="#059669"/><path d="M5 9l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : responseRemaining <= 0 ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="9" fill="#DC2626"/><path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" stroke="#2563EB" strokeWidth="1.5"/><path d="M9 5v4l2.5 2" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: respColor.text, fontFamily: font }}>First Response</div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: respColor.text, fontFamily: font, opacity: 0.8 }}>
                    {responseMet ? `Met · ${fmtElapsed(responseElapsed)}` : fmtRemaining(responseRemaining)}
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: respColor.text, fontFamily: font, background: "rgba(255,255,255,0.6)", borderRadius: 5, padding: "2px 8px", flexShrink: 0 }}>
                  Target 4h
                </div>
              </div>

              {/* Resolution */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", border: `1px solid ${resColor.border}`, borderRadius: 9, background: resColor.bg }}>
                <div style={{ flexShrink: 0 }}>
                  {resolutionMet ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="9" fill="#059669"/><path d="M5 9l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : resolutionRemaining <= 0 ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="9" fill="#DC2626"/><path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" stroke="#2563EB" strokeWidth="1.5"/><path d="M9 5v4l2.5 2" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: resColor.text, fontFamily: font }}>Resolution</div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: resColor.text, fontFamily: font, opacity: 0.8 }}>
                    {resolutionMet ? `Met · ${fmtElapsed(resolutionElapsed)}` : fmtRemaining(resolutionRemaining)}
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: resColor.text, fontFamily: font, background: "rgba(255,255,255,0.6)", borderRadius: 5, padding: "2px 8px", flexShrink: 0 }}>
                  Target 24h
                </div>
              </div>

            </div>
          </div>

          {/* Divider with arrow */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 4v12M6 12l4 4 4-4" />
              </svg>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", fontFamily: font, textTransform: "uppercase", letterSpacing: "0.06em" }}>converts to</span>
            </div>
            <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
          </div>

          {/* ── Work Order SLAs that will start ── */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", fontFamily: font, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
              Work Order SLA timers
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

              {/* Time to Onsite */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "11px 14px", border: "1px solid #E5E7EB", borderRadius: 9, background: "#F9FAFB" }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="8" r="6.5" />
                    <path d="M8 4.5V8l2 1.5" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: font }}>Time to Onsite</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#1D4ED8", background: "#DBEAFE", borderRadius: 4, padding: "1px 6px", fontFamily: font }}>Starts now</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#6B7280", fontFamily: font }}>Target 2h · begins when work order is created</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", fontFamily: font, background: "#E5E7EB", borderRadius: 5, padding: "2px 8px", flexShrink: 0 }}>2h</span>
              </div>

              {/* Time to Resolution */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "11px 14px", border: "1px solid #E5E7EB", borderRadius: 9, background: "#F9FAFB" }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="8" r="6.5" />
                    <path d="M8 4.5V8l2 1.5" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: font }}>Time to Resolution</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#059669", background: "#D1FAE5", borderRadius: 4, padding: "1px 6px", fontFamily: font }}>Starts on onsite</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#6B7280", fontFamily: font }}>Target 24h · begins when technician arrives on site</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", fontFamily: font, background: "#E5E7EB", borderRadius: 5, padding: "2px 8px", flexShrink: 0 }}>24h</span>
              </div>

            </div>
          </div>

          {/* Pause note */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 8 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M8 2l6 12H2L8 2z" />
              <path d="M8 7v3" />
              <circle cx="8" cy="11.5" r="0.5" fill="#D97706" stroke="none" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#92400E", fontFamily: font, lineHeight: "18px" }}>
              The ticket's <strong>Resolution SLA will pause</strong> while this work order is open, and resume automatically once the work order is completed.
            </span>
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #E5E7EB", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{ height: 38, padding: "0 18px", border: "1.5px solid #E5E7EB", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font }}
          >
            Cancel
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            style={{ height: 38, padding: "0 20px", border: "none", borderRadius: 8, background: "#0517F8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: font, display: "flex", alignItems: "center", gap: 7 }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="10" height="10" rx="1.5" />
              <path d="M5 7h4M7 5v4" />
            </svg>
            Create Work Order
          </button>
        </div>

      </div>
    </div>
  );
}

type SLARowState = "idle" | "running" | "paused" | "met" | "not_met";

function SLARow({
  byLabel, dueLabel, deadlineStr,
  state, startedAt, mountElapsed = 0, targetMinutes = 0,
}: {
  byLabel: string; dueLabel: string; deadlineStr: string;
  state: SLARowState; startedAt?: number; mountElapsed?: number; targetMinutes?: number;
}) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    if (state !== "running") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  let timerContent: React.ReactNode;
  if (state === "idle") {
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 500, color: "#a2aab8", fontFamily: "var(--font-family-base)" }}>—</span>
    );
  } else if (state === "met") {
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 700, color: "#00b09b", fontFamily: "var(--font-family-base)" }}>✓ Met</span>
    );
  } else if (state === "not_met") {
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 700, color: "#ee0064", fontFamily: "var(--font-family-base)" }}>✗ Not Met</span>
    );
  } else if (state === "paused") {
    const elapsed = mountElapsed;
    const remaining = targetMinutes - elapsed;
    const abs = Math.abs(remaining);
    const h = Math.floor(abs / 60);
    const m = Math.floor(abs % 60);
    timerContent = (
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round">
          <rect x="2" y="1.5" width="2.5" height="9" rx="1" fill="#D97706" stroke="none" />
          <rect x="7.5" y="1.5" width="2.5" height="9" rx="1" fill="#D97706" stroke="none" />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#D97706", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
          Paused · {h}h {m}m left
        </span>
      </div>
    );
  } else {
    const elapsed = mountElapsed + (startedAt != null ? (now - startedAt) / 60000 : 0);
    const remaining = targetMinutes - elapsed;
    const breached = remaining <= 0;
    const percentLeft = remaining / targetMinutes;
    const timerColor = breached ? "#ee0064" : percentLeft < 0.15 ? "#ee0064" : percentLeft < 0.35 ? "#ff7a00" : "#00b09b";
    const abs = Math.abs(remaining);
    const h = Math.floor(abs / 60);
    const m = Math.floor(abs % 60);
    const s = Math.floor((abs * 60) % 60);
    const countdown = `${breached ? "+" : ""}${h}h ${m}m ${String(s).padStart(2, "0")}s`;
    timerContent = (
      <span style={{ fontSize: 12, fontWeight: 700, color: timerColor, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
        {countdown}
      </span>
    );
  }

  return (
    <>
      {/* "Response by:" / "Resolution by:" row — lock icon + datetime */}
      <div style={{ display: "flex", alignItems: "center", width: "100%", minHeight: 32 }}>
        <div style={{ width: 140, display: "flex", alignItems: "center", gap: 8, flexShrink: 0, padding: "8px 0" }}>
          <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1.5" y="5.5" width="9" height="7" rx="1.5" stroke="#748094" strokeWidth="1.3"/>
              <path d="M4 5.5V4a2 2 0 1 1 4 0v1.5" stroke="#748094" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
            {byLabel}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0 8px", height: 32 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
            {deadlineStr}
          </span>
        </div>
      </div>
      {/* "Response due:" / "Resolution due:" row — timer icon + info icon + countdown */}
      <div style={{ display: "flex", alignItems: "center", width: "100%", minHeight: 32 }}>
        <div style={{ width: 140, display: "flex", alignItems: "center", gap: 8, flexShrink: 0, padding: "8px 0" }}>
          <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7.5" r="5.5" stroke="#748094" strokeWidth="1.3"/>
              <path d="M7 5v2.5l1.5 1.5" stroke="#748094" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5.5 1.5h3" stroke="#748094" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
            {dueLabel}
          </span>
          <div style={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="6.5" cy="6.5" r="5.5" stroke="#a2aab8" strokeWidth="1.2"/>
              <path d="M6.5 6v3.5" stroke="#a2aab8" strokeWidth="1.2" strokeLinecap="round"/>
              <circle cx="6.5" cy="4" r="0.7" fill="#a2aab8"/>
            </svg>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0 8px", height: 32 }}>
          {timerContent}
        </div>
      </div>
    </>
  );
}

function CommentBubble({ message }: { message: ChatMessage }) {
  if (message.isOutgoing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%" }}>
        <div style={{
          background: "#e6e8fe", borderRadius: "12px 12px 0 12px",
          padding: 12, maxWidth: 520,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              flex: 1, fontSize: 14, fontWeight: 700, lineHeight: "20px",
              letterSpacing: "-0.28px", color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)",
            }}>
              {message.sender}
            </span>
            <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
              <DotsIconLg />
            </button>
          </div>
          <p style={{
            fontSize: 14, fontWeight: 500, lineHeight: "20px",
            color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0,
          }}>
            {message.text}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
              {message.time}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
      <div style={{
        background: "#f3f4f6", borderRadius: "12px 12px 12px 0",
        padding: 12, maxWidth: 520,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        <span style={{
          fontSize: 14, fontWeight: 700, lineHeight: "20px",
          letterSpacing: "-0.28px", color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)",
        }}>
          {message.sender}
        </span>
        <p style={{
          fontSize: 14, fontWeight: 500, lineHeight: "20px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", margin: 0,
        }}>
          {message.text}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#748094", fontFamily: "var(--font-family-base)" }}>
            {message.time}
          </span>
        </div>
      </div>
    </div>
  );
}

function CommentsTab({ messages, onSend }: { messages: ChatMessage[]; onSend: (text: string) => void }) {
  return (
    <>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Internal banner */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 8,
          background: "#fff7f0", borderRadius: 8,
          padding: "8px 12px",
        }}>
          <div style={{ flexShrink: 0, marginTop: 1 }}>
            <InfoIcon />
          </div>
          <p style={{
            fontSize: 12, fontWeight: 500, lineHeight: "16px",
            color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
            margin: 0,
          }}>
            These comments are internal and only visible to your team members. Customers won't see anything posted here.
          </p>
        </div>

        {/* Date separator */}
        <div style={{ textAlign: "center" }}>
          <span style={{
            fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
            color: "#748094", fontFamily: "var(--font-family-base)",
          }}>
            Today, 15 April 2026
          </span>
        </div>

        {messages.map(m => <CommentBubble key={m.id} message={m} />)}
      </div>
      <ChatInput onSend={onSend} />
    </>
  );
}

function EmptyTabContent({ label }: { label: string }) {
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 8,
      color: "#a2aab8",
    }}>
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#e8eaed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="8" width="32" height="32" rx="6" />
        <path d="M16 20h16M16 26h10" />
      </svg>
      <p style={{ fontSize: 14, fontWeight: 500, fontFamily: "var(--font-family-base)", margin: 0 }}>
        No {label} yet
      </p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

interface TicketDetailPageProps {
  ticketId: string;
  onBack: () => void;
  linkedWoId?: string;
  onAddWorkOrder: () => void;
  onOpenWorkOrder: (woId: string) => void;
}

export function TicketDetailPage({ ticketId, onBack, linkedWoId, onAddWorkOrder, onOpenWorkOrder }: TicketDetailPageProps) {
  const [activeTab, setActiveTab] = React.useState<TicketTab>("External Chat");
  const [detailsCollapsed, setDetailsCollapsed] = React.useState(false);
  const [customFieldsCollapsed, setCustomFieldsCollapsed] = React.useState(false);
  const [messages, setMessages] = React.useState<ChatMessage[]>(EXTERNAL_CHAT_MESSAGES);
  const [commentMessages, setCommentMessages] = React.useState<ChatMessage[]>(COMMENT_MESSAGES);

  const ticket = TICKET;
  const [ticketStatus, setTicketStatus] = React.useState(ticket.status);
  const [ticketStatusColor, setTicketStatusColor] = React.useState(ticket.statusColor);
  const [hasReplied, setHasReplied] = React.useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
  const [showSLAHandoff, setShowSLAHandoff] = React.useState(false);
  const [woPausedAt, setWoPausedAt] = React.useState<number | null>(null);
  const slaStartRef = React.useRef(Date.now());

  type TicketActivityType = 'created' | 'status_changed' | 'timer_started' | 'timer_stopped' | 'sla_timer_started' | 'sla_met' | 'sla_paused' | 'sla_resumed';
  interface TicketActivityEntry { id: string; type: TicketActivityType; message: string; timestamp: string; user?: string; }
  function ticketNowStamp() {
    const now = new Date();
    return now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' at ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  const [activityLog, setActivityLog] = React.useState<TicketActivityEntry[]>([
    { id: 'act-init',              type: 'created',           message: 'Ticket created',                                 timestamp: ticketNowStamp(), user: 'James McDonald' },
    { id: 'act-sla-resp-start',   type: 'sla_timer_started', message: 'Response SLA timer started · Target 4h',         timestamp: ticketNowStamp() },
    { id: 'act-sla-res-start',    type: 'sla_timer_started', message: 'Resolution SLA timer started · Target 24h',      timestamp: ticketNowStamp() },
  ]);
  function addActivityEntry(type: TicketActivityType, message: string, user?: string) {
    setActivityLog(prev => [{ id: `act-${Date.now()}`, type, message, timestamp: ticketNowStamp(), user }, ...prev]);
  }

  const TICKET_STATUS_OPTIONS = [
    { label: "OPEN",        bg: "#ccefeb", color: "#00b09b" },
    { label: "IN PROGRESS", bg: "#fff0e6", color: "#ff7a00" },
    { label: "CLOSED",      bg: "#f3f4f6", color: "#748094" },
  ];

  function handleTicketStatusChange(opt: typeof TICKET_STATUS_OPTIONS[0]) {
    addActivityEntry('status_changed', `Status changed to ${opt.label}`, 'James McDonald');
    if (opt.label === "IN PROGRESS") addActivityEntry('sla_timer_started', 'Response SLA timer running');
    if (opt.label === "CLOSED") addActivityEntry('sla_met', 'Resolution SLA met ✓');
    setTicketStatus(opt.label);
    setTicketStatusColor({ bg: opt.bg, color: opt.color });
    setStatusDropdownOpen(false);
  }

  const TABS: { key: TicketTab; label: string; count?: number }[] = [
    { key: "External Chat", label: "External Chat" },
    { key: "Comments",      label: "Comments" },
    { key: "Email",         label: "Email", count: 2 },
    { key: "Activity Log",  label: "Activity Log" },
  ];

  function handleSend(text: string) {
    setMessages(prev => [...prev, {
      id: String(Date.now()),
      sender: "You", senderInitials: "YU",
      text, time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
      isOutgoing: true,
    }]);
    if (!hasReplied) {
      setHasReplied(true);
      addActivityEntry('sla_met', 'Response SLA met ✓ · First reply sent');
    }
  }

  return (
    <>
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100vh", overflow: "hidden",
      background: "white",
    }}>
      {/* Page Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 24px", height: 56, flexShrink: 0,
        borderBottom: "1px solid var(--color-border-primary)",
        background: "white",
      }}>
        {/* Left: menu + breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", padding: 0 }}>
            <MenuIcon />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={onBack}
              style={{
                border: "none", background: "transparent", cursor: "pointer", padding: "4px 0",
                fontSize: 12, fontWeight: 700, color: "#748094",
                fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
              }}
            >
              Tickets
            </button>
            <SlashIcon />
            <span style={{
              fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
            }}>
              {ticket.title}
            </span>
          </div>
        </div>

        {/* Right: actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button style={{
            height: 36, padding: "0 12px",
            border: "1px solid var(--color-border-primary)", borderRadius: 8,
            background: "white", cursor: "pointer",
            fontSize: 12, fontWeight: 700, color: "var(--color-content-primary)",
            fontFamily: "var(--font-family-base)",
          }}>
            Invite
          </button>
          <button style={{
            width: 36, height: 36,
            border: "1px solid var(--color-border-primary)", borderRadius: 8,
            background: "white", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <DotsIcon />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ── Left column ── */}
        <div style={{
          flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}>

          {/* Ticket header */}
          <div style={{
            display: "flex", alignItems: "center", gap: 16,
            padding: "16px 24px", flexShrink: 0,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: 10, background: "#e6e8fe",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <TicketTypeIcon />
            </div>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
              <h1 style={{
                margin: 0, fontSize: 18, fontWeight: 700, lineHeight: "28px",
                letterSpacing: "-0.36px", color: "var(--color-content-primary)",
                fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
              }}>
                {ticket.title}
              </h1>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FacilityIcon />
                  <span style={{
                    fontSize: 14, fontWeight: 500, color: "#0517f8",
                    fontFamily: "var(--font-family-base)",
                  }}>
                    {ticket.company}
                  </span>
                </div>
                <span style={{ color: "#a2aab8", fontSize: 14 }}>•</span>
                <span style={{
                  fontSize: 14, fontWeight: 500, color: "#748094",
                  fontFamily: "var(--font-family-base)",
                }}>
                  #{ticket.number}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            display: "flex", gap: 0, flexShrink: 0,
            borderBottom: "1px solid var(--color-border-primary)",
            padding: "0 24px",
          }}>
            {TABS.map(({ key, label, count }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    padding: "12px 8px 0", marginRight: 4,
                    border: "none", background: "transparent", cursor: "pointer",
                    display: "flex", flexDirection: "column", gap: 12, alignItems: "center",
                    fontFamily: "var(--font-family-base)",
                  }}
                >
                  <span style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
                    color: active ? "var(--color-content-primary)" : "#a2aab8",
                    display: "flex", gap: 4, alignItems: "center",
                  }}>
                    {label}
                    {count !== undefined && <span>({count})</span>}
                  </span>
                  <div style={{
                    height: 2, width: "100%", borderRadius: "2px 2px 0 0",
                    background: active ? "#0517f8" : "transparent",
                  }} />
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {activeTab === "External Chat" && (
              <ExternalChatTab messages={messages} onSend={handleSend} />
            )}
            {activeTab === "Comments" && (
              <CommentsTab
                messages={commentMessages}
                onSend={(text) => setCommentMessages(prev => [...prev, {
                  id: String(Date.now()), sender: "You", senderInitials: "YU",
                  text, time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
                  isOutgoing: true,
                }])}
              />
            )}
            {activeTab === "Email" && <WOEmailsTab woNumber={ticket.id} />}
            {activeTab === "Activity Log" && (
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 0 }}>
                {activityLog.length === 0 ? (
                  <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "var(--font-family-base)" }}>No activity yet.</span>
                ) : activityLog.map((entry, idx) => {
                  const colorMap: Record<TicketActivityType, string> = {
                    created:           "#F3F4F6",
                    status_changed:    "#EFF6FF",
                    timer_started:     "#D1FAE5",
                    timer_stopped:     "#FFF7ED",
                    sla_timer_started: "#EFF6FF",
                    sla_met:           "#D1FAE5",
                    sla_paused:        "#FFFBEB",
                    sla_resumed:       "#F0FDF4",
                  };
                  const iconMap: Record<TicketActivityType, React.ReactNode> = {
                    created: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#6B7280" strokeWidth="1.4" strokeLinecap="round"><rect x="1" y="1" width="10" height="10" rx="2" /><path d="M4 6h4M6 4v4" /></svg>
                    ),
                    status_changed: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#0517F8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="5" /><path d="M4 6h4M7 4.5L8.5 6 7 7.5" /></svg>
                    ),
                    timer_started: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6.5" r="4.5" /><path d="M6 4.5v2l1.5 1" /><path d="M4.5 1.5h3" /></svg>
                    ),
                    timer_stopped: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#EA580C" strokeWidth="1.4" strokeLinecap="round"><circle cx="6" cy="6.5" r="4.5" /><rect x="4" y="4.5" width="4" height="4" rx="0.5" fill="#EA580C" stroke="none" /></svg>
                    ),
                    sla_timer_started: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#2563EB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6.5" r="4.5" /><path d="M6 4.5v2l1.5 1" /><path d="M4.5 1.5h3" /></svg>
                    ),
                    sla_met: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="5" /><path d="M3.5 6l2 2 3-3" /></svg>
                    ),
                    sla_paused: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" strokeWidth="1.4" strokeLinecap="round"><rect x="2" y="1.5" width="2.5" height="9" rx="1" fill="#D97706" stroke="none" /><rect x="7.5" y="1.5" width="2.5" height="9" rx="1" fill="#D97706" stroke="none" /></svg>
                    ),
                    sla_resumed: (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#059669" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2l7 4-7 4V2z" fill="#059669" stroke="none" /></svg>
                    ),
                  };
                  return (
                    <div key={entry.id} style={{ display: "flex", gap: 10, position: "relative" }}>
                      {idx < activityLog.length - 1 && (
                        <div style={{ position: "absolute", left: 11, top: 24, bottom: -4, width: 1, background: "#E5E7EB" }} />
                      )}
                      <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: "50%", background: colorMap[entry.type], display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2, zIndex: 1 }}>
                        {iconMap[entry.type]}
                      </div>
                      <div style={{ flex: 1, paddingBottom: idx < activityLog.length - 1 ? 14 : 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "var(--font-family-base)", lineHeight: 1.4 }}>{entry.message}</div>
                        {entry.user && <div style={{ fontSize: 11, color: "#6B7280", fontFamily: "var(--font-family-base)", marginTop: 1 }}>{entry.user}</div>}
                        <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "var(--font-family-base)", marginTop: 2 }}>{entry.timestamp}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right sidebar ── */}
        <div style={{
          width: 340, flexShrink: 0,
          borderLeft: "1px solid var(--color-border-primary)",
          background: "white", overflowY: "auto",
          display: "flex", flexDirection: "column",
        }}>

          {/* Details */}
          <SidebarSection
            title="Details"
            collapsible
            collapsed={detailsCollapsed}
            onToggle={() => setDetailsCollapsed(v => !v)}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <DetailRow icon={<IdIcon />} label="Ticket ID">
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                  {ticket.number}
                </span>
              </DetailRow>
              <DetailRow icon={<CalendarIcon />} label="Created at">
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                  {ticket.createdAt}
                </span>
              </DetailRow>
              <DetailRow icon={<UserIcon />} label="Created by">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar initials={ticket.createdBy.initials} size={20} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                    {ticket.createdBy.name}
                  </span>
                </div>
              </DetailRow>
              <DetailRow icon={<FieldIcon />} label="Ticket Type">
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                  {ticket.ticketType}
                </span>
              </DetailRow>
              <DetailRow icon={<FieldIcon />} label="Status">
                <div style={{ position: "relative" }}>
                  <div style={{ cursor: "pointer" }} onClick={() => setStatusDropdownOpen(v => !v)}>
                    <StatusBadge label={ticketStatus} bg={ticketStatusColor.bg} color={ticketStatusColor.color} />
                  </div>
                  {statusDropdownOpen && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, zIndex: 100,
                      background: "white", border: "1px solid var(--color-border-primary)",
                      borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      minWidth: 140, overflow: "hidden",
                    }}>
                      {TICKET_STATUS_OPTIONS.map(opt => (
                        <div
                          key={opt.label}
                          onClick={() => handleTicketStatusChange(opt)}
                          style={{
                            padding: "8px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600,
                            fontFamily: "var(--font-family-base)", color: opt.color,
                            background: ticketStatus === opt.label ? opt.bg : "transparent",
                          }}
                          onMouseEnter={e => { if (ticketStatus !== opt.label) e.currentTarget.style.background = "#f9fafb"; }}
                          onMouseLeave={e => { if (ticketStatus !== opt.label) e.currentTarget.style.background = "transparent"; }}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DetailRow>
              <DetailRow icon={<UserIcon />} label="Assignee">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar initials={ticket.assignee.initials} size={20} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                    {ticket.assignee.name}
                  </span>
                </div>
              </DetailRow>
              <DetailRow icon={<TeamIcon />} label="Team">
                <span style={{
                  display: "inline-flex", padding: "4px 6px", borderRadius: 4,
                  background: "#f3f4f6", fontSize: 12, fontWeight: 700,
                  color: "#748094", fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
                }}>
                  {ticket.team}
                </span>
              </DetailRow>
            </div>
          </SidebarSection>

          {/* Assets */}
          <SidebarSection title="Assets" action={{ label: "Assign", onClick: () => {} }}>
            <div style={{
              border: "1px solid var(--color-border-primary)", borderRadius: 8,
              padding: 12, display: "flex", alignItems: "center", gap: 12,
              marginRight: 16,
            }}>
              <AssetImagePlaceholder />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  margin: 0, fontSize: 14, fontWeight: 700, lineHeight: "20px",
                  letterSpacing: "-0.28px", color: "var(--color-content-primary)",
                  fontFamily: "var(--font-family-base)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  Acme Asset 2000
                </p>
                <p style={{
                  margin: 0, fontSize: 12, fontWeight: 500, color: "#748094",
                  fontFamily: "var(--font-family-base)",
                }}>
                  00000XP
                </p>
              </div>
              <button style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex" }}>
                <DotsIconLg />
              </button>
            </div>
          </SidebarSection>

          {/* Work Orders */}
          <SidebarSection
            title="Work Orders"
            action={!linkedWoId ? { label: "Add", onClick: () => setShowSLAHandoff(true) } : undefined}
          >
            {linkedWoId ? (
              <div
                onClick={() => onOpenWorkOrder(linkedWoId)}
                style={{
                  border: "1px solid var(--color-border-primary)", borderRadius: 8,
                  padding: 12, display: "flex", alignItems: "center", gap: 12,
                  marginRight: 16, cursor: "pointer",
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: "#e6f7f4",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#00b09b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="12" height="12" rx="2" />
                    <path d="M5 8h6M5 5.5h4M5 10.5h3" />
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: 14, fontWeight: 700, lineHeight: "20px",
                    letterSpacing: "-0.28px", color: "var(--color-content-primary)",
                    fontFamily: "var(--font-family-base)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    Rotary Machine Spinner Repair
                  </p>
                  <p style={{
                    margin: 0, fontSize: 12, fontWeight: 500, color: "#748094",
                    fontFamily: "var(--font-family-base)",
                  }}>
                    WO-2026-0001
                  </p>
                </div>
                <span style={{
                  padding: "3px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: "#ccefeb", color: "#00b09b",
                  fontFamily: "var(--font-family-base)", flexShrink: 0,
                }}>
                  OPEN
                </span>
              </div>
            ) : (
              <div style={{
                textAlign: "center", padding: "16px 0",
                fontSize: 12, fontWeight: 500, color: "#a2aab8",
                fontFamily: "var(--font-family-base)",
              }}>
                No work orders linked
              </div>
            )}
          </SidebarSection>

          {/* SLA */}
          <SidebarSection title="SLA">
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <SLARow
                byLabel="Response by:"
                dueLabel="Response due:"
                deadlineStr="12/06/2026 03:52 am"
                state={hasReplied ? "met" : "running"}
                startedAt={slaStartRef.current}
                mountElapsed={3 * 60 + 17}
                targetMinutes={4 * 60}
              />
              <SLARow
                byLabel="Resolution by:"
                dueLabel="Resolution due:"
                deadlineStr="13/06/2026 11:52 am"
                state={ticketStatus === "CLOSED" ? "met" : woPausedAt != null ? "paused" : "running"}
                startedAt={woPausedAt == null ? slaStartRef.current : undefined}
                mountElapsed={21 * 60 + 17 + (woPausedAt != null ? (woPausedAt - slaStartRef.current) / 60000 : 0)}
                targetMinutes={24 * 60}
              />
            </div>
            {/* WO pause notice */}
            {woPausedAt != null && ticketStatus !== "CLOSED" && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "8px 0 4px", padding: "8px 10px", background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 7 }}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="#D97706" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path d="M6.5 1.5l5 9.5h-10l5-9.5z" />
                  <path d="M6.5 5.5v2.5" />
                  <circle cx="6.5" cy="9.5" r="0.4" fill="#D97706" stroke="none" />
                </svg>
                <span style={{ fontSize: 11, fontWeight: 500, color: "#92400E", fontFamily: "var(--font-family-base)", lineHeight: "16px" }}>
                  Resolution SLA <strong>paused</strong> — work order is open. Resumes when WO is completed.
                </span>
              </div>
            )}
          </SidebarSection>

          {/* Custom Fields */}
          <SidebarSection
            title="Ticket Custom Fields"
            collapsible
            collapsed={customFieldsCollapsed}
            onToggle={() => setCustomFieldsCollapsed(v => !v)}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              {[
                { label: "Select Property", value: "CUSTOM", isTag: true },
                { label: "Select Property", value: "CUSTOM", isTag: true },
                { label: "Select Property", value: "CUSTOM", isTag: true },
                { label: "Text Property",   value: "FL-500-0001", isTag: false },
                { label: "Text Property",   value: "FL-500-0001", isTag: false },
                { label: "Text Property",   value: "FL-500-0001", isTag: false },
              ].map((field, i) => (
                <DetailRow key={i} icon={<FieldIcon />} label={field.label}>
                  {field.isTag ? (
                    <span style={{
                      display: "inline-flex", padding: "4px 6px", borderRadius: 4,
                      background: "#f6f7ff", fontSize: 12, fontWeight: 700,
                      color: "#6974fb", fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
                    }}>
                      CUSTOM
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)" }}>
                      {field.value}
                    </span>
                  )}
                </DetailRow>
              ))}
            </div>
            <button style={{
              border: "none", background: "transparent", cursor: "pointer",
              padding: "4px 0", marginTop: 4,
              fontSize: 12, fontWeight: 700, color: "#0517f8",
              fontFamily: "var(--font-family-base)", letterSpacing: "-0.24px",
            }}>
              Show Empty Fields
            </button>
          </SidebarSection>

        </div>
      </div>
    </div>

    {showSLAHandoff && (
      <SLAHandoffModal
        onConfirm={() => { onAddWorkOrder(); setWoPausedAt(Date.now()); addActivityEntry('sla_paused', 'Resolution SLA paused · Work order created'); }}
        onClose={() => setShowSLAHandoff(false)}
        hasReplied={hasReplied}
        ticketStatus={ticketStatus}
        slaStartRef={slaStartRef}
      />
    )}
    </>
  );
}
